import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

/** Minus / number input / plus with quick-fill chips. Keeps a text draft so the field can be cleared while typing. */
export function NumField({ label, hint, value, onChange, min, max, step = 1, unit, chips, inputId }: {
  label: ReactNode;
  hint?: ReactNode;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  chips?: { label: ReactNode; value: number }[];
  inputId?: string;
}) {
  const [txt, setTxt] = useState(String(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setTxt(String(value)); }, [value]);
  const set = (n: number) => { const c = clamp(Math.round(n), min, max); onChange(c); setTxt(String(c)); };
  return (
    <div className="tr-numfield">
      <span className="field-label">{label}</span>
      <div className="tr-stepper">
        <button type="button" className="tr-step" aria-label="-" disabled={value <= min} onClick={() => set(value - step)}>−</button>
        <span className="tr-step-input">
          <input id={inputId} className="input" type="text" inputMode="numeric" value={txt}
            onFocus={(e) => { focused.current = true; e.target.select(); }}
            onBlur={() => { focused.current = false; setTxt(String(value)); }}
            onChange={(e) => {
              setTxt(e.target.value);
              const n = Number(e.target.value.replace(',', '.'));
              if (e.target.value.trim() !== '' && Number.isFinite(n)) onChange(clamp(Math.round(n), min, max));
            }} />
          {unit && <span className="tr-unit">{unit}</span>}
        </span>
        <button type="button" className="tr-step" aria-label="+" disabled={value >= max} onClick={() => set(value + step)}>+</button>
      </div>
      {chips && (
        <div className="tr-chips">
          {chips.map((c, i) => (
            <button key={i} type="button" className={`tr-chip ${value === clamp(c.value, min, max) ? 'on' : ''}`} onClick={() => set(c.value)}>{c.label}</button>
          ))}
        </div>
      )}
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

/** Scrollable sheet body + always-visible footer (use inside `<Sheet fixed>`). */
export function SheetLayout({ children, foot }: { children: ReactNode; foot?: ReactNode }) {
  return (
    <>
      <div className="tr-scroll">{children}</div>
      {foot && <div className="tr-foot">{foot}</div>}
    </>
  );
}

/** A tappable/plain list row card: headline + meta, with optional leading icon and trailing slot. */
export function Row({ lead, title, meta, trail, onClick, tone, className = '' }: {
  lead?: ReactNode; title: ReactNode; meta?: ReactNode; trail?: ReactNode; onClick?: () => void; tone?: 'gold' | 'red' | 'green'; className?: string;
}) {
  const inner = (
    <>
      {lead && <span className="tr-row-lead" aria-hidden="true">{lead}</span>}
      <span className="tr-row-main">
        <span className="tr-row-title">{title}</span>
        {meta && <span className="tr-row-meta">{meta}</span>}
      </span>
      {trail && <span className="tr-row-trail">{trail}</span>}
    </>
  );
  const cls = `tr-row ${tone ? `tone-${tone}` : ''} ${className}`;
  return onClick
    ? <button type="button" className={`${cls} tappable`} onClick={onClick}>{inner}</button>
    : <div className={cls}>{inner}</div>;
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="tr-skel" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => <span key={i} className="tr-skel-row" />)}
    </div>
  );
}

/** Dot-separated small facts. Falsy entries are skipped. */
export function Meta({ items }: { items: (ReactNode | false | null | undefined)[] }) {
  const list = items.filter((x) => x !== false && x !== null && x !== undefined && x !== '');
  return <>{list.map((x, i) => <span key={i} className="tr-meta-item">{x}</span>)}</>;
}
